import {
  createContext,
  type ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useSelector } from 'react-redux';
import { useHistory, useLocation, useParams } from 'react-router-dom';

import { useShowToast } from '@/hooks';
import { useKFlags } from '@/hooks/use-k-flags';
import { selectCurrUser } from '@/store/bootstrap/selectors';
import { useRunDemoHarnessMutation } from '@/store/query/scheduling';
import { type InterviewType } from '@/types/interview';
import { type AssistantMode } from './interviewer-assistant.types';

export type InterviewDetailView = 'details' | 'assistant';

interface InterviewStateLike {
  isBeforeInterview: boolean;
  isAfterInterview: boolean;
  isDuringInterview: boolean;
}

export interface InterviewerAssistantContextValue {
  view: InterviewDetailView;
  mode: AssistantMode;
  defaultMode: AssistantMode;
  defaultCopilotOpen: boolean;
  isHeaderCompact: boolean;
  isCurrentUserInterviewer: boolean;
  prepEnabled: boolean;
  prepChatEnabled: boolean;
  liveEnabled: boolean;
  liveReady: boolean;
  coachEnabled: boolean;
  coachReady: boolean;
  showAssistant: boolean;
  demoHarnessAvailable: boolean;
  demoRunning: boolean;
  isDemoModalOpen: boolean;
  isStartingDemo: boolean;
  openDetails: () => void;
  openAssistant: (mode?: AssistantMode, copilot?: boolean) => void;
  setMode: (mode: AssistantMode) => void;
  setHeaderCompact: (compact: boolean) => void;
  openDemoModal: () => void;
  closeDemoModal: () => void;
  startDemo: (durationMin: number) => Promise<void>;
}

const InterviewerAssistantContext =
  createContext<InterviewerAssistantContextValue | null>(null);

interface InterviewerAssistantProviderProps {
  children: ReactNode;
  interview: InterviewType;
  refetch: () => void;
  interviewState: InterviewStateLike;
}

export function InterviewerAssistantProvider({
  children,
  interview,
  refetch,
  interviewState,
}: InterviewerAssistantProviderProps) {
  const history = useHistory();
  const location = useLocation();
  const toast = useShowToast();
  const currUser = useSelector(selectCurrUser);
  const { pInterviewerAssistant, tDemoInterviewHarness } = useKFlags() as {
    pInterviewerAssistant?: boolean;
    tDemoInterviewHarness?: boolean;
  };
  const { interviewId, assistantMode: deepLinkMode } = useParams<{
    interviewId: string;
    assistantMode?: AssistantMode;
  }>();
  const routeInterviewId = interviewId ?? String(interview.id);
  const [runDemoHarness, { isLoading: isStartingDemo }] =
    useRunDemoHarnessMutation();

  // Cancelled interviews keep their original panel in `participants`, while the backend correctly
  // turns off `interviewer_assistant_accessible` because no Assistant surface remains available.
  // Keep identity separate from availability so a former panel member is redirected silently rather
  // than being told they were never part of the interview.
  const isCurrentUserListedInterviewer = !!interview.participants?.some(
    (participant) => participant.id === currUser?.id
  );
  const hasAssistantAccess =
    interview.interviewer_assistant_accessible ??
    isCurrentUserListedInterviewer;
  const isCurrentUserInterviewer =
    hasAssistantAccess || isCurrentUserListedInterviewer;
  const [view, setView] = useState<InterviewDetailView>('details');
  const [mode, setModeState] = useState<AssistantMode>('prep');
  const [defaultCopilotOpen, setDefaultCopilotOpen] = useState(false);
  const [isHeaderCompact, setHeaderCompact] = useState(false);
  const [isDemoModalOpen, setDemoModalOpen] = useState(false);
  // Demo readiness belongs to this mounted interview session only. Persisting it made a completed
  // demo unlock Live Coach again after a refresh, including for an interview rescheduled into the future.
  const [demoRunning, setDemoRunning] = useState(false);
  const [wasLiveThisSession, setWasLiveThisSession] = useState(false);

  const coachReady =
    hasAssistantAccess &&
    !!pInterviewerAssistant &&
    ['processing', 'completed'].includes(interview.intelligence_status) &&
    !!interview.interviewer_assistant_enabled;
  const liveReady =
    hasAssistantAccess &&
    !!pInterviewerAssistant &&
    !!interview.interviewer_assistant_enabled &&
    !!interview.interview_intelligence_session_id &&
    (demoRunning ||
      interviewState.isDuringInterview ||
      (interviewState.isAfterInterview && wasLiveThisSession));
  const prepEnabled =
    hasAssistantAccess && !!pInterviewerAssistant && !!interview.prep_available;
  const prepChatEnabled = prepEnabled && !interviewState.isAfterInterview;
  const liveEnabled =
    hasAssistantAccess &&
    !!pInterviewerAssistant &&
    !!interview.interviewer_assistant_enabled &&
    (!interviewState.isAfterInterview || wasLiveThisSession);
  const coachEnabled = hasAssistantAccess && !!pInterviewerAssistant;
  const defaultMode: AssistantMode =
    coachReady && !wasLiveThisSession ? 'coach' : liveReady ? 'live' : 'prep';
  const showAssistant = prepEnabled || liveReady || coachReady;
  const demoHarnessAvailable =
    hasAssistantAccess && !!tDemoInterviewHarness && !!interview.meeting_link;

  const refetchRef = useRef(refetch);
  refetchRef.current = refetch;

  useEffect(() => {
    if (liveReady) setWasLiveThisSession(true);
  }, [liveReady]);

  useEffect(() => {
    if (!liveReady || coachReady) return;
    const id = window.setInterval(() => refetchRef.current?.(), 12_000);
    return () => window.clearInterval(id);
  }, [liveReady, coachReady]);

  const recordingUrl = interview.recording_url;
  useEffect(() => {
    if (!coachReady || recordingUrl) return;
    let attempts = 0;
    const id = window.setInterval(() => {
      attempts += 1;
      refetchRef.current?.();
      if (attempts >= 20) window.clearInterval(id);
    }, 12_000);
    return () => window.clearInterval(id);
  }, [coachReady, recordingUrl]);

  useEffect(() => {
    if (!demoRunning || liveReady) return;
    let attempts = 0;
    const id = window.setInterval(() => {
      attempts += 1;
      refetchRef.current?.();
      if (attempts >= 12) window.clearInterval(id);
    }, 2_500);
    return () => window.clearInterval(id);
  }, [demoRunning, liveReady]);

  useEffect(() => {
    if (view === 'details') setHeaderCompact(false);
  }, [view]);

  const assistantPath = useCallback(
    (nextMode: AssistantMode) => `/interviews/${routeInterviewId}/${nextMode}`,
    [routeInterviewId]
  );
  const openAssistant = useCallback(
    (nextMode: AssistantMode = defaultMode, copilot = false) => {
      setModeState(nextMode);
      setDefaultCopilotOpen(copilot);
      setView('assistant');
      const nextPath = assistantPath(nextMode);
      const nextSearch = copilot ? '?copilot=1' : '';
      if (location.pathname !== nextPath || location.search !== nextSearch) {
        history.push(`${nextPath}${nextSearch}`);
      }
    },
    [assistantPath, defaultMode, history, location.pathname, location.search]
  );
  const openDetails = useCallback(() => {
    setView('details');
    setDefaultCopilotOpen(false);
    const nextPath = `/interviews/${routeInterviewId}`;
    if (location.pathname !== nextPath || location.search) {
      history.push(nextPath);
    }
  }, [history, location.pathname, location.search, routeInterviewId]);
  const setMode = useCallback(
    (nextMode: AssistantMode) => {
      setModeState(nextMode);
      setDefaultCopilotOpen(false);
      const nextPath = assistantPath(nextMode);
      if (location.pathname !== nextPath || location.search) {
        history.push(nextPath);
      }
    },
    [assistantPath, history, location.pathname, location.search]
  );

  useEffect(() => {
    if (!deepLinkMode) {
      setView('details');
      setDefaultCopilotOpen(false);
      return;
    }
    if (!isCurrentUserInterviewer) {
      history.replace(`/interviews/${routeInterviewId}`);
      toast({
        id: 'interviewer-assistant-access-denied',
        title: 'This page is private to the interview panel.',
        status: 'error',
      });
      return;
    }
    if (!pInterviewerAssistant || !hasAssistantAccess) {
      history.replace(`/interviews/${routeInterviewId}`);
      return;
    }

    const wantsCopilot =
      new URLSearchParams(location.search).get('copilot') === '1';
    setModeState(deepLinkMode);
    setDefaultCopilotOpen(wantsCopilot);
    setView('assistant');
  }, [
    deepLinkMode,
    hasAssistantAccess,
    history,
    isCurrentUserInterviewer,
    location.search,
    pInterviewerAssistant,
    routeInterviewId,
    toast,
  ]);

  const startDemo = useCallback(
    async (durationMin: number) => {
      const result = (await runDemoHarness({
        interviewId: interview.id,
        durationMin,
      })) as { error?: unknown };
      if (result?.error) {
        toast({
          title: 'Could not start the demo interview',
          status: 'error',
        });
        return;
      }

      setDemoModalOpen(false);
      setDemoRunning(true);
      openAssistant('live');
      toast({
        title: 'Demo interview started',
        description:
          'Join the Meet and admit the 3 Kula bots — the Live Coach lights up when they start talking.',
        status: 'success',
        duration: 8000,
      });
    },
    [interview.id, openAssistant, runDemoHarness, toast]
  );

  const value = useMemo<InterviewerAssistantContextValue>(
    () => ({
      view,
      mode,
      defaultMode,
      defaultCopilotOpen,
      isHeaderCompact,
      isCurrentUserInterviewer,
      prepEnabled,
      prepChatEnabled,
      liveEnabled,
      liveReady,
      coachEnabled,
      coachReady,
      showAssistant,
      demoHarnessAvailable,
      demoRunning,
      isDemoModalOpen,
      isStartingDemo,
      openDetails,
      openAssistant,
      setMode,
      setHeaderCompact,
      openDemoModal: () => setDemoModalOpen(true),
      closeDemoModal: () => setDemoModalOpen(false),
      startDemo,
    }),
    [
      coachEnabled,
      coachReady,
      defaultCopilotOpen,
      defaultMode,
      demoHarnessAvailable,
      demoRunning,
      isCurrentUserInterviewer,
      isDemoModalOpen,
      isHeaderCompact,
      isStartingDemo,
      liveEnabled,
      liveReady,
      mode,
      openAssistant,
      openDetails,
      prepEnabled,
      prepChatEnabled,
      setMode,
      showAssistant,
      startDemo,
      view,
    ]
  );

  return (
    <InterviewerAssistantContext.Provider value={value}>
      {children}
    </InterviewerAssistantContext.Provider>
  );
}

// Provider and hook intentionally share this module so consumers cannot import
// a context value without its lifecycle rules.
// eslint-disable-next-line react-refresh/only-export-components
export function useInterviewerAssistant() {
  const context = useContext(InterviewerAssistantContext);
  if (!context) {
    throw new Error(
      'useInterviewerAssistant must be used inside InterviewerAssistantProvider'
    );
  }
  return context;
}
